import clsx from "clsx";
import DragPreviewLayer from "designSystem/dnd/components/DragPreviewLayer";
import { DndStoreContext } from "designSystem/dnd/index";
import { Box, FlexColumn, FlexRow, FlexRowCenter, Text } from "designSystem/index";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, ReactElement } from "react";
import { AttributeDragPreview } from "./AttributeDragPreview";
import { AttributeGroupDragPreview } from "./AttributeGroupDragPreview";
import AttributesActions from "./AttributesActions";
import AttributesContainers from "./AttributesContainers";
import AttributesLevel from "./AttributesLevel";
import AttributesSearch from "./AttributesSearch";
import CustomViewAttribute from "./CustomViewAttribute";
import CustomViewAttributeGroup from "./CustomViewAttributeGroup";
import attributesSelectionsCss from "./CustomViewAttributes.module.css";
import { useCustomViewAttributesStore } from "./CustomViewAttributesStore";
import FlexColumnDropWrapper from "./FlexColumnDropWrapper";

function customViewAttributePreviewComponent(attribute: CustomViewAttribute): ReactElement {
	return <AttributeDragPreview attribute={attribute} />;
}

function customViewAttributeGroupPreviewComponent(group: CustomViewAttributeGroup): ReactElement {
	return <AttributeGroupDragPreview group={group} />;
}

function customViewAttributeMultiSelectPreviewComponent(attributes: CustomViewAttribute[]): ReactElement {
	return <AttributeGroupDragPreview attributes={attributes} />;
}

export const AttributesEditor: FunctionComponent = observer(() => {
	const {
		addMultiSelectedAttributes,
		attributeDndStore,
		showAddMultipleAttributes,
	} = useCustomViewAttributesStore();
	const { dragItem, multiSelectItemArray } = attributeDndStore;

	let dragPreviewComponent;
	if (multiSelectItemArray.length) {
		dragPreviewComponent = customViewAttributeMultiSelectPreviewComponent;
	} else if (dragItem instanceof CustomViewAttribute) {
		dragPreviewComponent = customViewAttributePreviewComponent;
	} else {
		dragPreviewComponent = customViewAttributeGroupPreviewComponent;
	}

	return (
		<DndStoreContext.Provider value={attributeDndStore}>
			<FlexRow
				data-csi-automation="AttributesEditor"
				fullHeight
				sx={{ userSelect: "none" }}
			>
				<FlexColumn
					borderRight={STANDARD_BORDER_STYLE}
					data-csi-automation="AvailableAttributes"
					width="50%"
				>
					<FlexColumn
						borderBottom={STANDARD_BORDER_STYLE}
						flexGrow={0}
						gap={1}
						pb={1}
						pt={3}
						px={6}
					>
						<FlexRowCenter gap={2}>
							<AttributesLevel />
						</FlexRowCenter>
						<FlexColumn gap={1}>
							<AttributesSearch />
							<AttributesActions />
						</FlexColumn>
					</FlexColumn>
					<Box growNoOverflow>
						<AttributesContainers />
						<DragPreviewLayer previewComponent={dragPreviewComponent} />
					</Box>
					{showAddMultipleAttributes && (
						<Box
							className={clsx(
								attributesSelectionsCss.multipleAttributesButton,
								attributesSelectionsCss.addMultipleAttributesText,
							)}
							onClick={addMultiSelectedAttributes}
						>
							<Text
								res="CustomViewAttributesAddSelections"
								resParams={[multiSelectItemArray.length]}
								semiBold
							/>
						</Box>
					)}
				</FlexColumn>
				<FlexColumnDropWrapper />
			</FlexRow>
		</DndStoreContext.Provider>
	);
});

export default AttributesEditor;